import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, LinkButton } from "../../../../components/Button";
import ConfirmModal from "../../../../components/ConfirmModal";
import * as Fonts from "../../../../components/Fonts";
import Input from "../../../../components/Form/Input/input";
import { FormGroup } from "../../../../components/Form/styles";
import { SubHeader } from "../../../../components/SubHeader";
import { useAppState } from "../../../../contexts/App";
import { api } from "../../../../services/api";
import { TrackingIntegrations } from "../../../../services/api/models";
import { Card, CardFooter, Container, Intro, RemoveLink, Section } from "./styles";

type AdditionalField = {
  title: string;
  propertyName: keyof TrackingIntegrations;
};

type Props = {
  title: string;
  helpDescription: string;
  providerName: keyof TrackingIntegrations;
  image: string;
  additionalField?: AdditionalField;
};

export const TrackingPage = ({ title, helpDescription, providerName, image, additionalField }: Props) => {
  const { t } = useTranslation();
  const { appState, setAppState } = useAppState();

  const tracking = useMemo(
    () => appState.editingSiteTrackingIntegrations || {},
    [appState.editingSiteTrackingIntegrations]
  );

  const [isRemoving, setRemoving] = useState(false);
  const [value, setValue] = useState(tracking[providerName]);
  const [additionalFieldValue, setAdditionalFieldValue] = useState(
    additionalField ? tracking[additionalField?.propertyName] : undefined
  );

  const [isSaving, setSaving] = useState(false);
  const isActive = !!tracking[providerName];

  const hasUnsavedChanges = useMemo(
    () =>
      tracking[providerName] !== value ||
      (additionalField && additionalFieldValue !== tracking[additionalField.propertyName]),
    [additionalField, additionalFieldValue, providerName, tracking, value]
  );

  const updateHandler = useCallback(
    async (value?: string) => {
      setSaving(true);
      const siteId = appState.editingSiteId as string;
      try {
        const updated = {
          ...tracking,
          [providerName]: value,
        };
        if (additionalField) {
          updated[additionalField.propertyName] = additionalFieldValue;
        }
        await api.updateTrackingIntegrations(siteId, updated);
        setAppState({
          ...appState,
          editingSiteTrackingIntegrations: updated,
        });
        // Stays on the screen, like /settings: the status above switches to "Ativo"/"Não configurado"
        setValue(value);
        if (additionalField) {
          setAdditionalFieldValue(updated[additionalField.propertyName]);
        }
      } finally {
        setSaving(false);
      }
    },
    [appState, tracking, providerName, additionalField, setAppState, additionalFieldValue]
  );

  const canSave = !!hasUnsavedChanges && !!value && !isSaving;

  const saveHandler = useCallback(() => {
    if (canSave) {
      updateHandler(value);
    }
  }, [canSave, updateHandler, value]);

  const discardChanges = useCallback(() => {
    setValue(tracking[providerName]);
    if (additionalField) {
      setAdditionalFieldValue(tracking[additionalField.propertyName]);
    }
  }, [additionalField, providerName, tracking]);

  const deleteHandler = useCallback(async () => {
    setRemoving(false);
    if (additionalField) {
      setAdditionalFieldValue(undefined);
    }
    updateHandler(undefined);
  }, [updateHandler]);

  return (
    <Container>
      <SubHeader
        title={title}
        crumbs={[{ label: t("pages.settings.header.title"), to: "/settings" }]}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      <Section>
        <Intro $active={isActive}>
          <div className="top">
            <img src={image} height={48} width={48} alt="" />
            <div className="text">
              <h6>{title}</h6>
              <span className="status">
                {isActive ? t("pages.settings.tracking.status.active") : t("pages.settings.tracking.status.inactive")}
              </span>
            </div>
          </div>
          <p>{helpDescription}</p>
        </Intro>
      </Section>

      <Section>
        <Fonts.Label>{t("pages.settings.tracking.sections.setup")}</Fonts.Label>
        <Card
          as="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            saveHandler();
          }}>
          <FormGroup>
            <Input
              id="id"
              type="text"
              value={value}
              label={title}
              onChange={e => setValue(e.target.value)}
              required={!!additionalField}
            />
          </FormGroup>
          {additionalField && (
            <FormGroup>
              <Input
                id={additionalField.propertyName}
                type="text"
                value={additionalFieldValue}
                label={additionalField.title}
                onChange={e => setAdditionalFieldValue(e.target.value)}
              />
            </FormGroup>
          )}
          <CardFooter>
            {hasUnsavedChanges && (
              <LinkButton onClick={discardChanges}>{t("pages.settings.unsavedBar.discard")}</LinkButton>
            )}
            <Button type="INFO" size="MD" disabled={!canSave} onClick={saveHandler}>
              {isSaving ? t("pages.settings.actions.save.button.titleSaving") : t("actions.save")}
            </Button>
          </CardFooter>
        </Card>
      </Section>

      {isActive && (
        <RemoveLink type="button" onClick={() => setRemoving(true)}>
          {t("pages.settings.tracking.remove.link")}
        </RemoveLink>
      )}

      {isRemoving && (
        <ConfirmModal
          type="DANGER"
          title={t("pages.settings.tracking.remove.confirmModal.title")}
          confirmButtonTitle={t("pages.settings.tracking.remove.confirmModal.confirmButton")}
          cancelTitle={t("pages.settings.tracking.remove.confirmModal.cancelButton")}
          onCancel={() => setRemoving(false)}
          onConfirm={deleteHandler}
        />
      )}
    </Container>
  );
};
